/**
 * Bulk Operations Helper
 * 
 * Responsible for handling bulk database operations like upsert, delete,
 * and mark for deletion with proper transaction management and error handling.
 */

import { prepareRecordForSync, validateRecordUuid, handleRecordDependencies } from '../../utils/uuidHelper';
import dbChangeNotifier from './dbChangeNotifier';

class BulkOperationsHelper {
    /**
     * Performs bulk upsert of records into a table
     * @param {IDBDatabase} db - Database instance
     * @param {string} tableName - Name of the table
     * @param {Array<Object>} records - Records to upsert
     * @param {Date} [currentSyncStartTime] - Current sync start time (for server updates)
     * @param {boolean} [isUserInitiated=true] - Whether the upsert was initiated by user action
     * @returns {Promise<Array>} - Upserted records
     */
    async performUpsert(db, tableName, records, currentSyncStartTime, isUserInitiated = true) {
        if (!Array.isArray(records) || records.length === 0) {
            return [];
        }

        try {
            const transaction = db.transaction(tableName, 'readwrite');
            const store = transaction.objectStore(tableName);

            // Process records in batches to handle large datasets
            const processedRecords = [];
            let hasCircleUpdates = false;

            try {
                // Special handling for SystemConfig table - needs direct check
                if (tableName === 'SystemConfig') {
                    for (const record of records) {
                        // For server records, preserve their timestamp exactly
                        const isServerRecord = record._fromServer === true;
                        
                        // Only update lastModified for user-initiated local changes
                        const shouldUpdateTimestamp = !isServerRecord && !currentSyncStartTime && isUserInitiated;
                        
                        // Ensure the record has a UUID and lastModified timestamp
                        const preparedRecord = prepareRecordForSync(record, tableName, shouldUpdateTimestamp);
                        
                        // If it's a server record, make sure we keep the exact server timestamp
                        if (isServerRecord && record.lastModified) {
                            preparedRecord.lastModified = record.lastModified;
                            // Clean up temporary flag
                            delete preparedRecord._fromServer;
                        }
                        
                        await this.handleSystemConfigRecord(store, preparedRecord, currentSyncStartTime);
                        processedRecords.push(preparedRecord);
                    }

                    await new Promise((resolve, reject) => {
                        transaction.oncomplete = resolve;
                        transaction.onerror = (e) => reject(e.target.error);
                    });

                    // Notify change if user initiated
                    if (isUserInitiated) {
                        dbChangeNotifier.notifyChange(tableName, processedRecords, 'upsert', isUserInitiated);
                    }

                    return processedRecords;
                }

                // Regular handling for other tables
                await Promise.all(records.map(async (record) => {
                    // Determine if this is a server-sourced record
                    const isServerRecord = record._fromServer === true;
                    
                    // Only update timestamp for user-initiated local changes
                    const shouldUpdateTimestamp = !isServerRecord && !currentSyncStartTime && isUserInitiated;
                    
                    // First ensure the record has a UUID and validate it
                    const preparedRecord = prepareRecordForSync(record, tableName, shouldUpdateTimestamp);
                    
                    // If it's a server record, make sure we keep the exact server timestamp
                    if (isServerRecord && record.lastModified) {
                        preparedRecord.lastModified = record.lastModified;
                        
                        // Remove the internal flag before storing
                        delete preparedRecord._fromServer;
                        
                        // console.log(`[bulkOperationsHelper] Preserving server timestamp ${preparedRecord.lastModified} for ${tableName} record ${preparedRecord.uuid}`);
                    }
                    
                    // Validate UUID for syncable tables
                    validateRecordUuid(preparedRecord, tableName);
                    
                    // Handle dependencies between tables (e.g. Circle-Invitation)
                    const finalRecord = handleRecordDependencies(preparedRecord, tableName);
                    
                    // Check for Circle updates to handle relationship management
                    if (tableName === 'Circle') {
                        hasCircleUpdates = true;
                    }
                    
                    // Get existing record if it exists
                    let existingRecord = null;
                    const uuid = finalRecord.uuid;
                    
                    try {
                        // Check if the uuid index exists 
                        if (store.indexNames.contains('uuid')) {
                            // If index exists, use it for efficient lookup
                            const uuidRequest = store.index('uuid').get(uuid);
                            existingRecord = await new Promise((resolve, reject) => {
                                uuidRequest.onsuccess = () => resolve(uuidRequest.result);
                                uuidRequest.onerror = (e) => reject(e.target.error);
                            });
                        } else {
                            // Fallback if uuid index doesn't exist - get all and filter
                            console.warn(`[bulkOperationsHelper] uuid index missing in ${tableName}, using slower lookup method`);
                            const getAllRequest = store.getAll();
                            const allRecords = await new Promise((resolve, reject) => {
                                getAllRequest.onsuccess = () => resolve(getAllRequest.result);
                                getAllRequest.onerror = (e) => reject(e.target.error);
                            });
                            
                            // Find the record with matching UUID
                            existingRecord = allRecords.find(r => r.uuid === uuid);
                        }
                    } catch (error) {
                        console.warn(`Error looking up record with uuid ${uuid} in table ${tableName}:`, error);
                    }

                    // Preserve certain fields from existing record if doing a partial update
                    if (existingRecord) {
                        // Don't update isDeleted if it's not explicitly set in the new record
                        if (finalRecord.isDeleted === undefined && existingRecord.isDeleted !== undefined) {
                            finalRecord.isDeleted = existingRecord.isDeleted;
                        }
                        
                        // CRITICAL: For server records, preserve the timestamp exactly as provided
                        if (isServerRecord && existingRecord.lastModified && finalRecord.lastModified) {
                            // console.log(`[bulkOperationsHelper] Preserving server timestamp for ${tableName} record ${uuid}`);
                        }
                    }

                    // Always add/update the record
                    const request = store.put(finalRecord);
                    await new Promise((resolve, reject) => {
                        request.onsuccess = resolve;
                        request.onerror = (e) => reject(e.target.error);
                    });

                    processedRecords.push(finalRecord);
                }));

                await new Promise((resolve, reject) => {
                    transaction.oncomplete = resolve;
                    transaction.onerror = (e) => reject(e.target.error);
                });

                // Notify the change
                if (isUserInitiated) {
                    dbChangeNotifier.notifyChange(tableName, processedRecords, 'upsert', isUserInitiated);
                }

                return processedRecords;
            } catch (error) {
                console.error(`Error in upsertRecords for table ${tableName}:`, error);
                throw new Error(`Failed to upsert records in table ${tableName}: ${error.message}`);
            }
        } catch (error) {
            console.error(`Error opening transaction for table ${tableName}:`, error);
            throw new Error(`Failed to open transaction for table ${tableName}: ${error.message}`);
        }
    }

    /**
     * Helper method to handle SystemConfig records which need special treatment
     */
    async handleSystemConfigRecord(store, record, currentSyncStartTimeDT) {
        return new Promise((resolve, reject) => {
            // Get all records and manually find matching category and name
            const request = store.getAll();
            
            request.onsuccess = (event) => {
                const allRecords = event.target.result;
                const existingRecord = allRecords.find(r => 
                    r.category === record.category && r.name === record.name
                );
                
                if (existingRecord) {
                    // Update the existing record with new data but keep its uuid
                    const updatedRecord = {
                        ...existingRecord,
                        ...record,
                        uuid: existingRecord.uuid, // Keep the original uuid
                        lastModified: currentSyncStartTimeDT
                    };
                    store.put(updatedRecord);
                } else {
                    // No existing record, add the new one
                    const newRecord = {
                        ...record,
                        lastModified: currentSyncStartTimeDT
                    };
                    store.put(newRecord);
                }
                resolve();
            };
            
            request.onerror = (error) => {
                console.error('Error getting all SystemConfig records:', error);
                // Still try to add the record
                const newRecord = {
                    ...record,
                    lastModified: currentSyncStartTimeDT
                };
                store.put(newRecord);
                resolve();
            };
        });
    }

    /**
     * Performs bulk deletion of records from a table
     * @param {IDBDatabase} db - Database instance
     * @param {string} tableName - Name of the table
     * @param {Array<string>} uuids - Array of UUIDs to delete
     * @param {boolean} [isUserInitiated=false] - Whether this operation was initiated by user action
     * @returns {Promise<Array<string>>} - The deleted UUIDs
     */
    async performDelete(db, tableName, uuids, isUserInitiated = false) {
        if (!Array.isArray(uuids) || uuids.length === 0) {
            return [];
        }

        try {
            const transaction = db.transaction(tableName, 'readwrite');
            const store = transaction.objectStore(tableName);

            await new Promise((resolve, reject) => {
                transaction.oncomplete = () => resolve(uuids);
                transaction.onerror = () => reject(transaction.error);

                uuids.forEach(uuid => {
                    store.delete(uuid);
                });
            });

            // If this was a user initiated change, notify the change tracker
            if (isUserInitiated) {
                dbChangeNotifier.notifyChange(tableName, 'delete', { 
                    count: uuids.length,
                    isUserInitiated 
                });
            }

            return uuids;
        } catch (error) {
            console.error(`Error in deleteRecords for table ${tableName}:`, error);
            throw error;
        }
    }

    /**
     * Marks records for deletion instead of actually deleting them
     * This is typically used for synced tables
     * @param {IDBDatabase} db - Database instance
     * @param {string} tableName - Name of the table
     * @param {Array<string>} uuids - Array of UUIDs to mark as deleted
     * @param {boolean} [isUserInitiated=false] - Whether this operation was initiated by user action
     * @returns {Promise<Array>} - The updated records
     */
    async performMarkForDeletion(db, tableName, uuids, isUserInitiated = false) {
        if (!Array.isArray(uuids) || uuids.length === 0) {
            return [];
        }

        // Try to create the transaction
        let transaction;
        try {
            transaction = db.transaction(tableName, 'readwrite');
        } catch (transactionError) {
            console.error(`Failed to create transaction for ${tableName} during markForDeletion:`, transactionError);
            throw transactionError;
        }

        const store = transaction.objectStore(tableName);
        const records = [];

        await new Promise((resolve, reject) => {
            transaction.oncomplete = () => resolve();
            transaction.onerror = () => reject(transaction.error);

            // Process each UUID synchronously within the same transaction
            uuids.forEach(uuid => {
                const request = store.get(uuid);
                request.onsuccess = () => {
                    const record = request.result;
                    if (record) {
                        record.isDeleted = true;
                        record.lastModified = new Date().toISOString();
                        store.put(record);
                        records.push(record);
                    }
                };
                request.onerror = () => {
                    console.error(`Failed to get record ${uuid} for deletion marking`);
                };
            });
        });

        // If this was a user initiated change, notify the change tracker
        if (isUserInitiated) {
            dbChangeNotifier.notifyChange(tableName, 'markForDeletion', { 
                count: uuids.length,
                isUserInitiated 
            });
        }

        return records;
    }
}

export default new BulkOperationsHelper();